/**
 * Reads the identifiers Google Analytics keeps in the visitor's cookies, so the
 * backend can attribute a sale to the session that carried the campaign.
 *
 * The conversion itself is sent server-side, from the Stripe webhook reconcile.
 * That is the only place the sale is known to be true — the browser used to
 * measure it, which meant a closed tab or an ad blocker silently cost us a
 * conversion. But the identifiers only exist in the browser, so the browser
 * still has to hand them over on the way out.
 */

export type GaIds = {
    /** From `_ga`. Identifies the browser to GA4. */
    clientId?: string;
    /** From `_ga_<STREAM_ID>`. Ties the sale to the visit that produced it. */
    sessionId?: string;
};

/**
 * `_ga=GA1.1.1234567890.1699999999` → `1234567890.1699999999`
 * The first two segments are the cookie's own format version, not the id.
 */
const parseClientId = (value: string): string | undefined => {
    const parts = value.split('.');

    return parts.length >= 4 ? parts.slice(-2).join('.') : undefined;
};

/**
 * `_ga_ABCDE12345=GS1.1.1699999999.1.0.1699999999.0.0.0` → `1699999999`
 * The session id is the third field.
 */
const parseSessionId = (value: string): string | undefined => {
    const parts = value.split('.');

    return parts.length >= 3 ? parts[2] : undefined;
};

/**
 * Pure so it can be tested: the unit suite runs in a node environment with no
 * `document` to read from.
 *
 * The session cookie is found by prefix rather than by name. Its name embeds the
 * GA4 stream id, which is configured inside the GTM container and appears
 * nowhere in this repo — hardcoding it here would create a second place to
 * update whenever the stream changes, and fail silently when someone forgets.
 */
export const parseGaCookies = (cookieString: string): GaIds => {
    const ids: GaIds = {};

    for (const entry of cookieString.split(';')) {
        const separator = entry.indexOf('=');

        if (separator === -1) continue;

        const name = entry.slice(0, separator).trim();
        const value = entry.slice(separator + 1).trim();

        if (name === '_ga') {
            ids.clientId = parseClientId(value);
        } else if (name.startsWith('_ga_') && !ids.sessionId) {
            ids.sessionId = parseSessionId(value);
        }
    }

    return ids;
};

/**
 * Both fields are absent for anyone blocking analytics, and on the very first
 * page view before the GA tag has written its cookies. That is expected: the
 * checkout goes through either way and the conversion is simply not attributed.
 */
export const readGaIds = (): GaIds => {
    if (typeof document === 'undefined') return {};

    try {
        return parseGaCookies(document.cookie);
    } catch {
        // Cookie access throws in some restricted browser contexts.
        return {};
    }
};
